/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
   distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
   prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
   content, please contact the author for proper authorization and agreement.
   */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { TrashIcon } from '@heroicons/react/24/solid'
import { toast } from 'utils/toastUtils'

import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import { ContactsAction } from 'store/contacts/ContactsAction'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import {
    closeModal,
    openModal,
    closeSecondaryModal,
    openSecondaryModal,
    setHeadbarTitle,
} from 'helpers/actions'

import { UiTableCellTextPrimary } from 'components/Ui/UiTableCellText'
import UiTableHeaderLabel from 'components/Ui/UiTableHeaderLabel'
import UiTableRowActionsContainer from 'components/Ui/UiTableRowActionsContainer'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import UiBadge from 'components/Ui/UiBadge'
import AssignContactToCompanyForm from 'components/Contacts/AssignContactToCompanyForm'
import EditCompanyModal from 'components/Companies/EditCompanyModal'
import EditLeadModal from 'components/leads/EditLeadModal'
import CreateLeadModal from 'components/leads/CreateLeadModal'
import LeadsAction from 'store/leads/LeadsAction'
import { useHeadbarReminders } from 'utils/hooks/useHeadbarReminders'
import { useTable } from 'utils/hooks/useTable'
import { ModalSize } from 'types/enums/ModalSize'
import { UiTabSwitcherItem } from 'components/Ui/UiTabSwitcher'
import { Lead } from 'types/entities/Lead'
import { Offer } from 'types/entities/Offer'
import { Reservation } from 'types/entities/Reservation'
import { splitLeadsAndOpportunities } from 'utils/leadOpportunityUtils'
import { getCompanyDisplayName } from 'utils/companyUtils'
import { ContactDetailTab } from 'types/enums/specific/ContactDetailTab'
import { UnifiedRow } from 'types/entities/ContactUnifiedRow'

interface UseContactCompanyDetailParams {
    contactIdStr: string | undefined
}

export const useContactCompanyDetail = ({ contactIdStr }: UseContactCompanyDetailParams) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const contactsState = useTypedSelector((s) => s.contactsState)
    const contact = contactsState.selectedContact
    const companies = contactsState.selectedContactCompanies
    const leads = contactsState.selectedContactLeads
    const offers = contactsState.selectedContactOffers

    const [activeTab, setActiveTab] = useState<ContactDetailTab>(ContactDetailTab.Accounts)
    const columnHelper = createColumnHelper<UnifiedRow>()

    // --- Derived state ---

    const split = useMemo(() => splitLeadsAndOpportunities(leads || []), [leads])

    const reservations = useMemo<Reservation[]>(() => {
        const reservationMap = new Map<number, Reservation>()
        ;(offers || []).forEach((offer: Offer) => {
            const reservation = offer.reservation || offer.packagesCart?.reservation || null
            if (reservation?.id && !reservationMap.has(reservation.id)) {
                reservationMap.set(reservation.id, reservation)
            }
        })
        return Array.from(reservationMap.values())
    }, [offers])

    const tabItems: UiTabSwitcherItem[] = useMemo(
        () => [
            { id: ContactDetailTab.Accounts, name: `${t('Account')} (${companies?.length || 0})` },
            { id: ContactDetailTab.Leads, name: `${t('Leads')} (${split.leads.length})` },
            { id: ContactDetailTab.Opportunities, name: `${t('Opportunities')} (${split.opportunities.length})` },
            { id: ContactDetailTab.Offers, name: `${t('Offers')} (${offers?.length || 0})` },
            { id: ContactDetailTab.Reservations, name: `${t('Reservations')} (${reservations.length})` },
        ],
        [companies?.length, split.leads.length, split.opportunities.length, offers?.length, reservations.length, t]
    )

    const unifiedData: UnifiedRow[] = useMemo(() => {
        const c: UnifiedRow[] = (companies || []).map((item) => ({
            id: item.company_id,
            type: 'Company',
            name: getCompanyDisplayName(item.company) || t('Unknown Company'),
            details: [item.company?.city, item.company?.country].filter(Boolean).join(', '),
            original: item.company,
            contactCompany: item,
        }))
        const l: UnifiedRow[] = split.leads.map((item: Lead) => ({
            id: item.id,
            type: 'Lead',
            name: item.name,
            details: item.status,
            original: item,
        }))
        const p: UnifiedRow[] = split.opportunities.map((item: Lead) => ({
            id: item.id,
            type: 'Opportunity',
            name: item.name,
            details: item.status,
            original: item,
        }))
        const o: UnifiedRow[] = (offers || []).map((item: Offer) => ({
            id: item.id,
            type: 'Offer',
            name: `Offer #${item.number}`,
            details: item.status,
            original: item,
        }))
        const r: UnifiedRow[] = reservations.map((item: Reservation) => {
            const isPast =
                item.date && dayjs(item.date).isValid()
                    ? dayjs(item.date).isBefore(dayjs())
                    : false
            return {
                id: item.id,
                type: 'Reservation',
                name: `${t('Reservation')} #${item.id}`,
                details: `${item.date || '-'} • ${item.status || '-'}${isPast ? ` • ${t('Past event')}` : ''}`,
                original: item,
            }
        })

        if (activeTab === ContactDetailTab.Accounts) return c
        if (activeTab === ContactDetailTab.Leads) return l
        if (activeTab === ContactDetailTab.Opportunities) return p
        if (activeTab === ContactDetailTab.Offers) return o
        if (activeTab === ContactDetailTab.Reservations) return r
        return []
    }, [companies, split.leads, split.opportunities, offers, reservations, activeTab, t])

    // --- Handlers ---

    const handleUnassignClick = useCallback(
        (companyId: number) => {
            if (!contact) return
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Unassign Contact')}
                        message={t('Are you sure you want to unassign this contact from the company?')}
                        onConfirm={async () => {
                            try {
                                await dispatch(ContactsAction.fetchUnassignFromCompany(contact.id, companyId))
                                toast.success(t('Contact unassigned successfully'))
                                dispatch(closeSecondaryModal())
                            } catch {
                                toast.error(t('Failed to unassign contact'))
                                dispatch(closeSecondaryModal())
                            }
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
        },
        [contact, dispatch, t]
    )

    const handleRowClick = useCallback(
        (row: UnifiedRow) => {
            if (row.type === 'Company' && row.original) {
                dispatch(
                    openModal(<EditCompanyModal company={row.original} />, {
                        modalSize: ModalSize.RoundedFullscreen,
                        closeModal: () => {
                            dispatch(CompaniesAction.resetSelectedCompany())
                            dispatch(closeModal())
                        },
                    })
                )
            } else if ((row.type === 'Lead' || row.type === 'Opportunity') && row.original) {
                dispatch(
                    openModal(
                        <EditLeadModal
                            lead={row.original}
                            onSuccess={() => {
                                if (contactIdStr) {
                                    dispatch(ContactsAction.fetchContactById(parseInt(contactIdStr)))
                                }
                                dispatch(closeModal())
                                dispatch(setHeadbarTitle(null))
                                dispatch(LeadsAction.resetSelectedLead())
                            }}
                        />,
                        {
                            modalSize: ModalSize.RoundedFullscreen,
                            closeModal: () => {
                                dispatch(LeadsAction.resetSelectedLead())
                                dispatch(closeModal())
                            },
                        },
                        row.type === 'Opportunity' ? t('Edit Opportunity') : t('Edit Lead')
                    )
                )
            } else if (row.type === 'Offer' && row.id) {
                navigate(`/offers/${row.id}`)
            } else if (row.type === 'Reservation' && row.id) {
                navigate(`/reservations/detail/${row.id}`)
            }
        },
        [dispatch, navigate, contactIdStr, t]
    )

    const handleAssignClick = useCallback(() => {
        if (!contact) return
        dispatch(
            openSecondaryModal(
                <AssignContactToCompanyForm
                    contactId={contact.id}
                    contact={contact}
                    onSuccess={() => {
                        // Success is handled by push to state in action
                    }}
                />,
                undefined,
                t('Assign Contact to Company')
            )
        )
    }, [contact, dispatch, t])

    // --- Columns ---

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: () => <UiTableHeaderLabel>{t('Name')}</UiTableHeaderLabel>,
                cell: (info) => (
                    <div className="flex flex-col">
                        <UiTableCellTextPrimary>{info.getValue()}</UiTableCellTextPrimary>
                        {info.row.original.details && (
                            <span className="text-xs text-gray-500 font-medium">{info.row.original.details}</span>
                        )}
                    </div>
                ),
            }),
            columnHelper.accessor('type', {
                header: () => <UiTableHeaderLabel>{t('Type')}</UiTableHeaderLabel>,
                cell: (info) => (
                    <UiBadge
                        variant={
                            info.getValue() === 'Company'
                                ? 'info'
                                : info.getValue() === 'Lead' || info.getValue() === 'Opportunity'
                                  ? 'warning'
                                  : info.getValue() === 'Reservation'
                                    ? 'purple'
                                    : 'success'
                        }
                    >
                        {info.getValue()}
                    </UiBadge>
                ),
            }),
            columnHelper.display({
                id: 'actions',
                enableSorting: false,
                enableColumnFilter: false,
                cell: (info) => (
                    <UiTableRowActionsContainer>
                        {info.row.original.type === 'Company' && info.row.original.contactCompany && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (info.row.original.type === 'Company' && info.row.original.contactCompany) {
                                        handleUnassignClick(info.row.original.contactCompany.company_id)
                                    }
                                }}
                                className="p-1.5 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 rounded transition-colors shrink-0"
                                title={t('Unassign from company') as string}
                            >
                                <TrashIcon className="w-4 h-4 text-white" />
                            </button>
                        )}
                    </UiTableRowActionsContainer>
                ),
            }),
        ],
        [t, columnHelper, handleUnassignClick]
    )

    const tableInit = useTable<UnifiedRow>({
        defaultData: unifiedData,
        columns,
        defaultRecordsPerPage: 100,
    })

    const handleTabChange = useCallback((tabId: ContactDetailTab) => {
        setActiveTab(tabId)
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('table:reset-pagination-first-page'))
        }
    }, [])

    // --- Effects ---

    useHeadbarReminders(
        !!contactIdStr && !Number.isNaN(Number(contactIdStr)),
        'Contact',
        Number(contactIdStr)
    )

    useEffect(() => {
        tableInit.setRecordsTable(unifiedData)
        tableInit.setRecordsCount(unifiedData.length)
        tableInit.setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
        )
    }, [unifiedData, tableInit.setRecordsTable, tableInit.setRecordsCount, tableInit.setPagination])

    useEffect(() => {
        dispatch(setHeadbarTitle(t('Contacts')))
    }, [dispatch, t])

    useEffect(() => {
        if (contactIdStr) {
            dispatch(ContactsAction.resetSelectedContact())
            dispatch(ContactsAction.fetchContactById(parseInt(contactIdStr)))
        }
    }, [contactIdStr, dispatch])

    const handleCreateLead = useCallback(() => {
        if (!contact) {
            return
        }

        const primaryCompany = companies?.[0]?.company ?? null

        dispatch(
            openModal(
                <CreateLeadModal
                    company={primaryCompany}
                    predefinedContact={contact}
                    disableCompanyChange={Boolean(primaryCompany)}
                />,
                {
                    modalSize: ModalSize.RoundedFullscreen,
                    closeModal: () => dispatch(closeModal()),
                },
                t('Create Lead')
            )
        )
    }, [companies, contact, dispatch, t])

    return {
        contact,
        activeTab,
        setActiveTab: handleTabChange,
        tabItems,
        tableInit,
        handleAssignClick,
        handleRowClick,
        handleCreateLead,
    }
}
